<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.edit")) +
            " " +
            $tc("employees.title", 2)
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide"/>
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >

          <!-- employee id -->
          <validation-provider
            #default="validationContext"
            :name="$t('employees.employee_id')"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('employees.employee_id')"
              label-for="employee_id"
            >
              <b-form-input
                id="employee_id"
                v-model="itemData.employee_id"
                :state="getValidationState(validationContext)"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="image_url"
            rules=""
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('omnicloud_fr.face.image')"
              label-for="image"
            >
              <div class="custom-file-upload">
                <b-form-file
                  id="image"
                  v-model="itemData.imageFile"
                  :state="getValidationState(validationContext)"
                  accept="image/*"
                  placeholder="Choose a file or drop it here..."
                  drop-placeholder="Drop file here..."
                  browse-text="Browse"
                  class="tall-file-input"
                />
              </div>
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- first name -->
          <validation-provider
            #default="validationContext"
            :name="$t('employees.first_name')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('employees.first_name')"
              label-for="job_title"
            >
              <b-form-input
                id="first_name"
                v-model="itemData.first_name"
                :state="getValidationState(validationContext)"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- salary -->
          <validation-provider
            #default="validationContext"
            :name="$t('employees.last_name')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('employees.last_name')"
              label-for="last_name"
            >
              <b-form-input
                id="first_name"
                v-model="itemData.last_name"
                :state="getValidationState(validationContext)"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="id_tenant"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$tc('companies.title', 2)"
              label-for="id_company"
            >
              <v-select
                v-model="itemData.id_company"
                :options="companiesOptions"
                :clearable="true"
                :reduce="(val) => val.value"
                input-id="id_company"
              />

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <div v-if="itemData.image" class="d-flex justify-content-center align-items-center">
            <img              
              :src="itemData.image.url"
              class="img-fluid rounded"
              style="max-width: 150px; max-height: 150px;"
              :alt="itemData.image.name"
            />
          </div>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BInputGroupAppend,
} from "bootstrap-vue";
import {ValidationProvider, ValidationObserver} from "vee-validate";
import vSelect from "vue-select";
import {computed, ref, onMounted} from "@vue/composition-api";
import {required, alphaNum, email, positive, required_if} from "@validations";
import {useToast} from "vue-toastification/composition";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BInputGroupAppend,
    BButton,
    vSelect,
    BFormCheckbox,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    companiesOptions: {
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, {emit}) {
    //COMPOSABLES
    const toast = useToast();

    //DATA
    const blankData = {
      first_name: null,
      last_name: null,
      id_company: null,
      imageFile: null,
      employee_id: null,
    }

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const onSubmit = async () => {
      let dispatch;

      const formData = new FormData();

      if(itemData.value.imageFile) {
        formData.append('image', itemData.value.imageFile);
      }
      
      formData.append('first_name', itemData.value.first_name);
      formData.append('last_name', itemData.value.last_name);
      formData.append('id_company', itemData.value.id_company);
      
      if(itemData.value.employee_id) {
        formData.append('employee_id', itemData.value.employee_id);
      }
      
      if (props.isAdd) {
        dispatch = store.dispatch("employees/add", formData);
      } else {
        const req = {
          id: itemData.value.id,
          params: formData,
        };
        dispatch = store.dispatch("employees/postEdit", req);
      }

      dispatch
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });

            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const {refFormObserver, getValidationState, resetForm} = formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = {
          ...props.itemEdit,
          imageFile: null,
        };
      }
    };

    return {
      itemData,
      onSubmit,

      refFormObserver,
      getValidationState,
      loadData,
      resetForm
    };
  },
};
</script>
